import { useEffect, useRef, useCallback } from 'react';
import styled, { css } from 'styled-components';
import { useLocation } from 'react-router-dom';
import { useRouter } from '~/utils/navigation';
import { ButtonIcon } from '@comeon/ui';
import ComeonIcon, { ICON_ARROW_LEFT, ICON_CLOSE } from '~/components/icon/index.js';
import { getZindexByType } from '~/utils/style-utils.ts';
import useUIContext, { UIContextConstants } from '~/ui/use-ui-context.ts';
import { useCloseCurrentContext } from '~/hooks/use-close-context.js';

const dataAt = props => ({
	'data-at': props.dataAt
});

const PageHeader = styled.header.attrs(dataAt)`
	background-color: ${({ transparent }) => (transparent && 'transparent') || 'inherit'};
	display: flex;
	height: var(--sidebarHeaderHeight);
	left: 0;
	overflow: hidden;
	position: sticky;
	top: 0;
	white-space: nowrap;
	width: 100%;
	z-index: ${({ zIndex }) => zIndex};

	${({ noBorderBottom }) =>
		noBorderBottom &&
		css`
			border-bottom: 0;
		`}
`;

const ButtonWrapper = styled.div``;

const SidebarHeader = ({
	closeIcon = false,
	backIcon = false,
	children,
	Icon,
	transparent = false,
	transparentBackground = false,
	zIndex = getZindexByType('header'),
	noBorderBottom = false,
	className = '',
	shouldScroll,
	scrollThreshold = 100,
	titleRef,
	noHiddenTitle,
	onClose = null,
	isDisabled = false
}) => {
	const headerRef = useRef(null);
	const { contextRef, within } = useUIContext();
	const location = useLocation();
	const router = useRouter();

	useEffect(() => {
		const container = contextRef?.current;
		if (shouldScroll && container) {
			const staticTextHeaderScroll = () => {
				const staticTextContainer = container.scrollTop;
				if (staticTextContainer > scrollThreshold) {
					headerRef.current?.classList.add('page-head--opac');
					if (titleRef?.current?.classList) titleRef?.current?.classList?.remove('u-text-hide');
					if (transparent) {
						headerRef.current?.classList.remove('u-bg-trans');
					}
				} else {
					headerRef.current?.classList.remove('page-head--opac');
					if (!noHiddenTitle && titleRef?.current?.classList) {
						titleRef.current.classList.add('u-text-hide');
					}
					if (transparent) {
						headerRef.current?.classList.add('u-bg-trans');
					}
				}
			};
			container.addEventListener('scroll', staticTextHeaderScroll);
			return () => container.removeEventListener('scroll', staticTextHeaderScroll);
		}
	}, [contextRef, noHiddenTitle, scrollThreshold, shouldScroll, titleRef, transparent]);

	const closeCurrentContext = useCloseCurrentContext();
	const close = useCallback(
		event => {
			event.stopPropagation();
			if (onClose) {
				onClose();
			} else if (within === UIContextConstants.SIDEBAR && location?.state?.from) {
				router.replace(location.state.from);
			} else {
				closeCurrentContext();
			}
		},
		[router, location, closeCurrentContext, within, onClose]
	);

	const classes = `${className} page-head ${transparent ? ' u-bg-trans' : ''}`;

	return (
		<PageHeader
			className={classes}
			dataAt="sidebar-mobile-header-general"
			ref={headerRef}
			transparent={transparent || transparentBackground}
			zIndex={zIndex}
			noBorderBottom={noBorderBottom}
		>
			<ButtonWrapper className="page-head__left-side">
				{closeIcon && (
					<ButtonIcon disabled={isDisabled} onClick={close} dataAt="close-button-general">
						<ComeonIcon type={ICON_CLOSE} />
					</ButtonIcon>
				)}
				{backIcon && (
					<ButtonIcon disabled={isDisabled} onClick={close} dataAt="back-button-general">
						<ComeonIcon type={ICON_ARROW_LEFT} className="sidebar-header__icon--filter" />
					</ButtonIcon>
				)}
				{Icon && Icon}
			</ButtonWrapper>
			{children}
		</PageHeader>
	);
};

export default SidebarHeader;
